import { ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { AuthService, BreadcrumbService, CompanyViewService, ConfirmDialogService, ProductService, SearchThemeReplacementTagsOptions, StateService, ThemeService, Utils } from 'src/app/services';
import { MatSnackBar } from '@angular/material/snack-bar';
import { 
  Company, GetThemeRequest, GetThemeResponse, ListThemeLineItemsRequest, ListThemeLineItemsResponse, Product2, ProductType, 
  PropertyType, PublishThemeRequest, PublishThemeResponse, RoomType, ScopeVariant, SituViewWebApiClient, Theme, ThemeLineItem, 
  ThemeLineItemType, ScoredThemeReplacementTag, UpsertThemeLineItemsRequest
} from 'situview-webapi-client';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigService } from 'src/app/services/config.service';
import { ProductSelectorDialogComponent, ProductSelectorDialogData, ProductSelectorResultData, ArtifactViewerDialogComponent } from 'src/app/components';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Guid } from 'guid-typescript';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';
import { GetIfcSettingsRequest, IfcToJsonFurnishingSettings, SituWarpApiClient } from 'situ-warpapi-client';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';

@Component({
  selector: 'themes-detail',
  templateUrl: './themes-detail.component.html',
  styleUrls: ['./themes-detail.component.scss']
})

export class ThemesDetailComponent extends SituBaseComponent {
  /* Search */
  themeLineItemFilterTerm = "";

  /* Data */
  companyId: string | undefined;
  themeId: string | undefined;
  theme: Theme | undefined;
  productTypesMap: Map<string, ProductType> | undefined;
  productsMap: Map<string, Product2> | undefined;
  allCompaniesMap: Map<string, Company> | undefined;
  themeLineItemTypesMap: Map<string, ThemeLineItemType> | undefined;
  url: string | undefined;
  selectedProduct: Product2 | undefined;
  propertyTypes = PropertyType;

  builderIfcFurnishingSettings: Map<string, IfcToJsonFurnishingSettings> | undefined;

  isExpanded: { [key: string]: boolean } = {};

  showAutoFillTagOptions = false;

  productSelectorDialogRef: MatDialogRef<ProductSelectorDialogComponent> | undefined;
  artifactViewerDialogRef: MatDialogRef<ArtifactViewerDialogComponent> | undefined;

  groupedLineItems: LineItemGroup[] = [];
  filteredGroupedLineItems: LineItemGroup[] = [];
  scopes = RoomType;
  scopeVariants = ScopeVariant;

  autoFillTagSearchForm: FormGroup;
  areaGroups: Record<string, ProductType[]> = {};
  autoFillTagProductTypeControl = new FormControl();
  filteredAutoFillTagProductTypes: Observable<ProductType[]> | undefined;
  @ViewChild('productTypeInput') productTypeInput: ElementRef<HTMLInputElement> | undefined;

  // Mime type uses 'Web' format, see: https://developer.chrome.com/blog/web-custom-formats-for-the-async-clipboard-api/
  clipboardLineItemMimeType = 'web application/x-situ-theme-line-items';

  /* Query Parameters */
  readonly FilterParamName: string = "filter";
  readonly SituViewUrl: string;

  /* Loading */
  private _loading = false;
  debouncingSave = false;
  lastSave?: Date;

  saving = false;
  
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private situViewWebApiClient: SituViewWebApiClient,
    private situWarpApiClient: SituWarpApiClient,
    private route: ActivatedRoute,
    private router: Router,
    private breadcrumbService: BreadcrumbService,
    private snackBar: MatSnackBar,
    private themeService: ThemeService,
    private productService: ProductService,
    private configService: ConfigService,
    private companyViewService: CompanyViewService,
    private dialog: MatDialog,
    private dialogService: ConfirmDialogService,
    private fb: FormBuilder) {
    super(authService, stateService);

    this.themeId = this.route.snapshot.params['themeId'];
    this.companyId = this.route.snapshot.params['companyId'];
    this.SituViewUrl = configService.config.situViewWebApiBaseUrl;
    
    this._subscriptions.push(this.themeLineItemChange.pipe( debounceTime(2000) )
    .subscribe(() => {
      this.saveThemeLineItems();
    }))

    this.autoFillTagSearchForm = this.fb.group({
      filterByHomesInRelease: [true],
      filterActiveReleases: [true],
      filterByLatestIfcFiles: [true],
      useIfcTags: [false],
      ifcTagsOnly: [false],
      standardTagsOnly: [false],
      includeTagGroups: [[]],
      selectedProductTypes: [[]]
    });
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.filteredAutoFillTagProductTypes = this.autoFillTagProductTypeControl.valueChanges.pipe(
      startWith(''),
      map(value => this.filterProductTypes(value))
    );
  }

  protected onSelectedCompanyChange(): void {
    this.loading = true;

    this.load();
  }

  async load() {
    this.loading = true;

    try{
      // Load in a specific order do dependencies are loaded in the fastest way possible
      
      var loadCompaniesPromise = this.loadCompanies();
      var loadProductTypesPromise = this.loadProductTypes();
      var loadThemeLineItemTypesPromise = this.loadThemeLineItemTypes();

      await loadCompaniesPromise;
      var loadThemePromise = this.loadTheme();
      await this.loadBuilderIfcSettings();

      await loadThemePromise;
      await loadThemeLineItemTypesPromise;
      await this.loadThemeLineItems();

      await loadProductTypesPromise;

    } finally {
      this.loading = false;
    }
  }

  getRangeMin(range: string): number {
    if (!range) return 0; // Default minimum value
    const [min, max] = range.split('-').map(value => parseFloat(value.trim()));
    return isNaN(min) ? 0 : min;
  }
  
  getRangeMax(range: string): number {
    if (!range) return 100; // Default maximum value
    const [min, max] = range.split('-').map(value => parseFloat(value.trim()));
    return isNaN(max) ? 100 : max;
  }

  onTagSelected(group: LineItemGroup, scoredTag: ScoredThemeReplacementTag) {
    group.addTagToGroup(scoredTag);

    this.filterThemeLineItems();

    this.debounceSaveChanges();
  }
  
  onScopeSelected(group: LineItemGroup, scope: RoomType) {
    group.addScopeToGroup(scope);

    this.filterThemeLineItems();

    this.debounceSaveChanges();
  }

  onLineItemTypeChange(group: LineItemGroup) {
    group.showLineItemTypeSwap = false;
    var themeLineItemType = this.getLineItemType(group.lineItemTypeId);

    if (!themeLineItemType) {
      return;
    }

    group.tagGroup = themeLineItemType.themeReplacementTagGroup;

    for (const lineItem of group.lineItems) {
      lineItem.lineItemTypeId = group.lineItemTypeId;
    }

    for (const property of themeLineItemType.properties!) {
      group.lineItemProperties[property.propertyName!] = property.defaultValue;
    }
  }

  removeLineItemGroup(group: LineItemGroup) {
    this.groupedLineItems = this.groupedLineItems.filter(x => x !== group);

    this.filterThemeLineItems();

    this.debounceSaveChanges();
  }
  
  removeTagFromGroup(group: LineItemGroup, tag: string) {
    group.removeTagFromGroup(tag);

    this.filterThemeLineItems();

    this.debounceSaveChanges();
  }
  
  removeScopeFromGroup(group: LineItemGroup, scope?: RoomType, scopeVariant?: ScopeVariant) {
    group.removeScopeFromGroup(scope, scopeVariant);

    this.filterThemeLineItems();

    this.debounceSaveChanges();
  }

  allowAlternativeLineItemTypesForGroup(group: LineItemGroup) : boolean {
    if (!group.isExpanded || group.showLineItemTypeSwap) {
      return false;
    }

    if (!group.lineItemTypeId && !group.showLineItemTypeSwap) {
      return true;
    }

    var groupThemeLineItemType = this.getLineItemType(group.lineItemTypeId);
    var hasAlternative = [...this.themeLineItemTypesMap!.values()]
      .some(x => x !== groupThemeLineItemType && x.themeReplacementTagGroup === groupThemeLineItemType?.themeReplacementTagGroup);

    return hasAlternative;
  }

  getAlternativeLineItemTypesForGroup(group: LineItemGroup) : ThemeLineItemType[] {
    if (!group.lineItemTypeId && !group.tagGroup) {
      return [...this.themeLineItemTypesMap!.values()];
    }

    var lineItemTypes = [...this.themeLineItemTypesMap!.values()]
      .filter(x => x.themeReplacementTagGroup === group.tagGroup);

    return lineItemTypes;
  }
  
  getTagOptions(group: LineItemGroup) {
    if (group.allValidTags || group.hasRequestedTagOptions) {
      return;
    }

    group.hasRequestedTagOptions = true;
    
    var products = this.getProductIds(group).map(x => this.getProductById(x)).filter(x => x !== undefined) as Product2[];

    var options = new SearchThemeReplacementTagsOptions();
    options.themeLineItemTypeId = group.lineItemTypeId;
    options.tagGroupFilter = group.tagGroup ? [group.tagGroup] : undefined;
    options.productTypeIds = products.map(x => x.productTypeId).filter(x => x !== undefined) as string[];
    options.productSpecificationIds = products.map(x => x.productSpecificationIds).reduce((a, b) => (a || []).concat(b || []), []);

    if (this.theme?.companyId) {
      this.themeService.SearchThemeReplacementTags(this.theme.companyId, options)
        .then((tags: ScoredThemeReplacementTag[]) => {
          group.allValidTags = tags;
          group.filterTags();
        })
        .catch((err: any) => {
        });
    }
  }

  getProductById(productId: string | undefined) : Product2 | undefined {
    return productId ? this.productsMap?.get(productId) : undefined;
  }

  getLineItemType(lineItemTypeId: string | undefined) : ThemeLineItemType | undefined {
    return lineItemTypeId ? this.themeLineItemTypesMap?.get(lineItemTypeId) : undefined;
  }

  getLineItemTypeName(lineItemTypeId: string) {
    return this.getLineItemType(lineItemTypeId)?.name;
  }

  getCompany(companyId: string | undefined) : Company | undefined {
    return companyId ? this.allCompaniesMap?.get(companyId) : undefined;
  }

  getProductType(productTypeId: string | undefined) : ProductType | undefined {
    return productTypeId ? this.productTypesMap?.get(productTypeId) : undefined;
  }

  getBuilderIfcSettingTag(settingName: string | undefined) : string | undefined {
    return this.getBuilderIfcSetting(settingName)?.furnishingTag;
  }

  isBuilderIfcTag(settingName: string | undefined) : boolean {
    if (this.getBuilderIfcSetting(settingName)) {
      return true;
    }
    return false;
  }

  hasBuilderIfcTag(settingName: string | undefined) : boolean {
    if (!settingName) {
      return false;
    }

    if (this.getBuilderIfcSetting(settingName)) {
      return true;
    }
    if (Array.from(this.builderIfcFurnishingSettings!.values()).some(x => x.furnishingTag === settingName) ) {
      return true;
    }

    return false;
  }

  getBuilderIfcSetting(settingName: string | undefined) : IfcToJsonFurnishingSettings | undefined {
    return settingName ? this.builderIfcFurnishingSettings?.get(settingName) : undefined;
  }

  getIfcTags(tag: string | undefined) : string[] | undefined {
    var tag = this.getBuilderIfcSettingTag(tag) ?? tag;

    var tags = Array.from(this.builderIfcFurnishingSettings!.values())
      .filter(setting => setting.furnishingTag === tag && setting.furnishingName)
      .map(setting => setting.furnishingName!);
    
    tags.unshift(tag!);

    return tags;
  }

  getSortingTag(themeLineItem: ThemeLineItem) : string | undefined {
    var sortTag = '';

    if (!themeLineItem.tag) {
      sortTag = '\u0000'; // Sort null tags to top
    } else {
      var builderIfcSetting = this.getBuilderIfcSetting(themeLineItem.tag);
      if (builderIfcSetting) {
        sortTag = `${builderIfcSetting.furnishingTag}|${themeLineItem.tag}`;
      } else {
        sortTag = `${themeLineItem.tag}|\uffff`; // Standard tags go below IFC tags
      }
    }

    if (themeLineItem.scope) {
      sortTag = `${sortTag}|${RoomType[themeLineItem.scope]}`;
    }

    if (themeLineItem.scopeVariant) {
      sortTag = `${sortTag} ${ScopeVariant[themeLineItem.scopeVariant]}`;
    }

    //console.log(sortTag);
    return sortTag;
  }

  private getProductId(group: LineItemGroup, property?: string): string | undefined {
    var properties = group.lineItemProperties;
    if (property) return properties[property];

    var lineItemTypeProperties = this.getLineItemTypeProperties(group.lineItemTypeId);

    var key = lineItemTypeProperties.find(x => x.assetType === PropertyType.TEMPLATE)?.propertyName;
    return key ? properties[key] : undefined;
  }

  private getProductIds(group: LineItemGroup): string[] {
    var properties = group.lineItemProperties;

    var lineItemTypeProperties = this.getLineItemTypeProperties(group.lineItemTypeId);

    var keys = lineItemTypeProperties
      .filter(x => x.assetType === PropertyType.TEMPLATE)
      .map(x => x.propertyName!);
    return keys.reduce((acc: string[], key) => {
      if (properties[key]) {
        acc.push(properties[key]);
      }
      return acc;
    }, []);
  }

  hasProduct(group: LineItemGroup, property?: string): boolean {
    var productId = this.getProductId(group, property);
    return productId !== undefined;
  }
  
  getProductPreview(group: LineItemGroup, property?: string): string | undefined {
    var productId = this.getProductId(group, property);
    return this.getProductById(productId)?.previewArtifactId;
  }
  
  getProductName(group: LineItemGroup, property?: string): string | undefined {
    const productId = this.getProductId(group, property);
    return this.getProductById(productId)?.name;
  }
  
  getProductSku(group: LineItemGroup, property?: string): string | undefined {
    const productId = this.getProductId(group, property);
    return this.getProductById(productId)?.sku;
  }
  
  getProductTypeId(group: LineItemGroup, property?: string): string | undefined {
    const productId = this.getProductId(group, property);
    return this.getProductById(productId)?.productTypeId;
  }

  getProductTypeName(group: LineItemGroup, property?: string): string | undefined {
    const productTypeId = this.getProductTypeId(group, property);
    return this.getProductType(productTypeId)?.name;
  }
  
  getCompanyLogoByProduct(group: LineItemGroup, property?: string): string | undefined {
    const productId = this.getProductId(group, property);
    const product = this.getProductById(productId);
    return this.getCompanyLogo(product?.companyId);
  }
  
  getCompanyNameByProduct(group: LineItemGroup, property?: string): string | undefined {
    const productId = this.getProductId(group, property);
    const product = this.getProductById(productId);
    return this.getCompanyName(product?.companyId);
  }
  
  getCompanyLogo(companyId: string | undefined): string | undefined {
    return this.getCompany(companyId)?.logoUrl;
  }
  
  getCompanyName(companyId: string | undefined): string | undefined {
    return this.getCompany(companyId)?.name;
  }

  publishTheme() {
    const options: ConfirmDialogData = {
      title: `Publish the current theme ${this.theme?.name}?`,
      message: 'This will take a couple minutes',
      cancelText: 'Cancel',
      confirmText: 'Publish',
      actionIcon: 'priority_high',
      actionColour: 'warn'
    };
    this.dialogService.open(options);
    this._subscriptions.push(this.dialogService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        let request = new PublishThemeRequest();
        request.companyId = this.selectedCompany?.id;
        request.themeName = this.theme?.name;

        this.situViewWebApiClient.publishTheme(request)
          .then((response: PublishThemeResponse) => {
            if (response.success) {
              this.showMessage("The theme is currently being published, please allow a couple of minutes for it to process.");
            } else {
              this.showMessage(response.errorMessages![0]);
            }
          })
          .catch((err: any) => {
            this.showMessage(err);
          });
      }
    }));
  }

  addTag() {
    var newItem = new ThemeLineItem();
    newItem.properties = { };

    var lineItemGroup = new LineItemGroup(newItem);
    lineItemGroup.isExpanded = true;
    lineItemGroup.showLineItemTypeSwap = true;

    this.groupedLineItems.unshift(lineItemGroup); // Add the new group to the start of the list of groups
    this.filteredGroupedLineItems.unshift(lineItemGroup);
  }
  
  addProduct(group?: LineItemGroup, propertyName?: string, isAdd?: boolean, templateName?: string) {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
  
    const dialogWidth = `${Math.round((70 / 100) * screenWidth)}px`;
    const dialogHeight = `${Math.round((90 / 100) * screenHeight)}px`;

    var groupKey = group?.key;

    var dialogData = new ProductSelectorDialogData();
    dialogData.companyId = this.theme?.companyId;
    dialogData.templateNameFilter = templateName;
    dialogData.tagSelectionEnabled = isAdd;
    dialogData.groupKey = groupKey;
    dialogData.propertyName = propertyName;
    dialogData.tagGroupsFilter = group?.tagGroup ? [group.tagGroup] : undefined;

    if (group?.tags) {
      dialogData.relatedTagsOrder = [...group.tags].filter(x => x != undefined) as string[];
    }
  
    this.productSelectorDialogRef = this.dialog.open(ProductSelectorDialogComponent, {
      width: dialogWidth,
      height: dialogHeight,
      data: dialogData,
    });
  
    this._subscriptions.push(this.productSelectorDialogRef.afterClosed()
      .subscribe((data: ProductSelectorResultData) => {
        if (!data) {
          // Dialog was closed without triggering workflow
          return;
        }
        
        var product = data.products[0];
        if (product) {
          if (!this.productsMap?.has(product.id!)) {
            // If this is a new product that is not in the product map, add it.
            this.productsMap?.set(product.id!, product);
          }

          if (data.groupKey && data.propertyName && !data.tagSelectionEnabled) { //existing with replacements
            let group = this.groupedLineItems.find(x => x.key === groupKey);
            if (group) {
              group.changeProperty(data.propertyName, product.id);

              this.filterThemeLineItems();

              this.debounceSaveChanges();
            }
          } else {
            const themeLineItemType = this.getLineItemType(data.type?.id);
            if (themeLineItemType && product) { //only do something if a line type exists

              var tags = [...data.tags];
              var scopes = [new Scope(data.scope, data.scopeVariant)]

              if (data.groupKey) {
                var g = this.groupedLineItems.find(x => x.key === data.groupKey);

                if (g?.tags) {
                  tags = [...g.tags].filter(x => x != undefined) as string[];
                }
                
                if (g?.scopes) {
                  scopes = g.scopes;
                }
              }

              if (tags.length === 0) {
                tags.push("");
              }

              var newLineItems: ThemeLineItem[] = [];
              for (const tag of tags) {
                for (const scope of scopes) {
                  let themeLineItem = new ThemeLineItem();
                  themeLineItem.id = Guid.create().toString();
                  themeLineItem.lineItemTypeId = themeLineItemType.id;
                  themeLineItem.tag = tag ? tag : undefined;
                  themeLineItem.scope = scope.scope;
                  themeLineItem.scopeVariant = scope.scopeVariant;
    
                  var productType = this.getProductType(product.productTypeId);
                  var templateName = productType!.templateName!.toLowerCase();
  
                  themeLineItem.properties = {  };
                  themeLineItemType.properties!.forEach(x => {
                    if (x.templateName && x.templateName.toLowerCase() === templateName) {
                      themeLineItem.properties![x.propertyName!] = product.id;
                    } else {
                      themeLineItem.properties![x.propertyName!] = x.defaultValue;
                    }
                  });

                  newLineItems.push(themeLineItem);
                }
              }
              
              if (data.groupKey) {
                this.groupedLineItems = this.groupedLineItems.filter(x => x.key !== data.groupKey);
                this.filteredGroupedLineItems = this.filteredGroupedLineItems.filter(x => x.key !== data.groupKey);
              }

              this.groupLineItems(newLineItems, true);

              this.filterThemeLineItems();

              this.debounceSaveChanges();
            } else {
              this.showMessage("Sorry! We do not support that line type.");
            }
          }
        }
      }));
  }

  deleteProduct(group: LineItemGroup, property: string) {
    var properties = group.lineItemProperties;
    properties[property] = undefined;

    this.filterThemeLineItems();

    this.debounceSaveChanges();
  }

  onView3dPreview(previewArtifactId: string) {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
  
    const dialogWidth = `${Math.round((70 / 100) * screenWidth)}px`;
    const dialogHeight = `${Math.round((90 / 100) * screenHeight)}px`;

    if (previewArtifactId) {
      this.artifactViewerDialogRef = this.dialog.open(ArtifactViewerDialogComponent, {
        width: dialogWidth,
        height: dialogHeight,
        data: {
          fileName: "product.glb",
          artifactId: previewArtifactId
        }
      });
    }
  }

  duplicateLineItemTypeGroup(lineItemGroup: LineItemGroup) {
    var lineItem = new ThemeLineItem();
    lineItem.lineItemTypeId = lineItemGroup.lineItemTypeId;
    lineItem.properties = JSON.parse(JSON.stringify(lineItemGroup.lineItemProperties));

    var newLineItemGroup = new LineItemGroup(lineItem, lineItemGroup.tagGroup);
    newLineItemGroup.forceGroupSplit = true;
    newLineItemGroup.isExpanded = true;

    var origItemIndex = this.groupedLineItems.indexOf(lineItemGroup);
    this.groupedLineItems.splice(origItemIndex + 1, 0, newLineItemGroup);

    this.filterThemeLineItems();

    this.debounceSaveChanges();
  }

  private themeLineItemChange = new Subject();
  debounceSaveChanges(): void {
    this.debouncingSave = true;
    this.themeLineItemChange.next();
  }

  getThemeReplacementTagGroups(): Set<string> {
    const distinctGroups = new Set<string>();

    if (this.themeLineItemTypesMap) {
      for (const themeLineItemType of this.themeLineItemTypesMap.values()) {
        if (themeLineItemType.themeReplacementTagGroup) {
          distinctGroups.add(themeLineItemType.themeReplacementTagGroup);
        }
      }
    }

    return distinctGroups;
  }

  getLineItemTypeProperties(lineItemTypeId: string) {
    const lineItemType = this.getLineItemType(lineItemTypeId);
    if (lineItemType && lineItemType.properties) {
      Utils.dynamicSort(lineItemType.properties, x => x.assetType === PropertyType.TEMPLATE); // Put the products at the top of the list
      return lineItemType.properties;
    }
    return [];
  }

  private async loadTheme() {
    this.debouncingSave = false;

    let request = new GetThemeRequest();
    request.companyId = this.companyId;
    request.themeId = this.themeId;

    try {
      var response: GetThemeResponse = await this.situViewWebApiClient.getTheme(request);

      if (response.success && response.theme?.id) {
        this.theme = response.theme;
        this.lastSave = response.theme.dateModified;

        var themeCompany = this.getCompany(this.companyId);
        this.breadcrumbService.setParam("name", `${themeCompany!.name} - ${this.theme.name}`);
      } else {
        this.showMessage(`There was an issue loading the theme. Taking you back to the theme list.`);
        console.error(response.errorMessages);
        this.router.navigate(['/homes/themes']);
      }
    } catch(e) {
      this.showMessage(`There was an issue loading the theme. Taking you back to the theme list.`);
      console.error(e);
    }
  }

  private async saveThemeLineItems() {
    this.saving = true;
    this.debouncingSave = false;

    let request = new UpsertThemeLineItemsRequest();
    request.companyId = this.theme?.companyId;
    request.themeId = this.theme?.id;

    request.themeLineItems = [];
    this.groupedLineItems.forEach(e => {
      request.themeLineItems!.push(...e.lineItems);
    });

    Utils.dynamicSort(request.themeLineItems, x => this.getSortingTag(x));

    try {
      try {
        const response = await this.situViewWebApiClient.upsertThemeLineItems(request);
        if (response.success && response.themeLineItemResponses) {
        } else {
          this.showMessage(`There was an issue saving ${this.theme!.name}.`);
          console.error(response.errorMessages);
        }
      } catch (error) {
        this.showMessage(`There was an issue saving ${this.theme!.name}.`);
        console.error(error);
      }
    } finally {
      this.saving = false;
      this.lastSave = new Date();
    }
  }

  private async loadThemeLineItems() {
    let request = new ListThemeLineItemsRequest();
    request.companyId = this.companyId;
    request.themeId = this.themeId;
    
    try {
      var response: ListThemeLineItemsResponse = await this.situViewWebApiClient.listThemeLineItems(request);

      if (response.success && response.themeLineItems) {
        var lineItems = response.themeLineItems!;
        Utils.dynamicSort(lineItems, x => this.getSortingTag(x));
        this.groupLineItems(lineItems);

        await this.loadProducts();
    
        await this.filterThemeLineItems();
      } else {
        this.showMessage(`There was an issue loading the theme line items.`);
        console.error(response.errorMessages);
        return;
      }
    } catch(e) {
      this.showMessage(`There was an issue loading the theme line items.`);
      console.error(e);
    }
  }

  private groupLineItems(lineItems: ThemeLineItem[], addToStart = false) {
    var grouped = this.groupedLineItems

    // Group all line items that have the same properties and scopes
    lineItems.reduce((acc, item) => {
      var matchingLineItemGroup = acc.find(e => e.fitsGroup(item));

      if (matchingLineItemGroup) {
        matchingLineItemGroup.addToGroup(item);
      } else {
        var lineItemType = this.getLineItemType(item.lineItemTypeId);
        matchingLineItemGroup = new LineItemGroup(item, lineItemType?.themeReplacementTagGroup);

        if (addToStart) {
          acc.unshift(matchingLineItemGroup); // Add the new group to the start of the list of groups
        } else {
          acc.push(matchingLineItemGroup);
        }
      }
      return acc;
    }, grouped);

    // Merge all the groups with other groups that have the same properties and tags
    for (let a = 0; a < grouped.length; a++) {
      const groupA = grouped[a];
      for (let b = a + 1; b < grouped.length; b++) {
        const groupB = grouped[b];
        if (groupA !== groupB && groupA.canMergeGroup(groupB)) {
          groupA.mergeGroup(groupB);
          grouped.splice(b, 1);
          b--;
        }
      }
    }
  
    return grouped;
  }

  private async loadProducts(): Promise<void> {
    var allProductIds = new Set<string>();
    
    this.groupedLineItems
      .forEach(x => {
        var productIds = this.getProductIds(x);
        productIds.forEach(productId => allProductIds.add(productId))
      });

    var products = await this.productService.LoadProducts(this.companyId!, [...allProductIds])
    this.productsMap = new Map<string, Product2>(products.map(x => [x.id!, x]));
  }

  private async loadProductTypes() {
    const productTypes = await this.productService.LoadProductTypesWithColor();
    this.productTypesMap = new Map<string, ProductType>(productTypes.map(x => [x.id!, x]));
    
    this.buildAreaGroups(productTypes);

    this.autoFillTagProductTypeControl.setValue('');
  }

  private async loadCompanies() {
    const companies = await this.companyViewService.LoadCompanies();
    this.allCompaniesMap = new Map<string, Company>(companies.map(x => [x.id!, x]));
  }
  
  private async loadThemeLineItemTypes() {
    var themeLineItemTypes = await this.themeService.LoadThemeLineItemTypes();
    this.themeLineItemTypesMap = new Map<string, ThemeLineItemType>(themeLineItemTypes.map(x => [x.id!, x]));
  }

  private async loadBuilderIfcSettings() {
    var request = new GetIfcSettingsRequest();
    request.companyCode = this.getCompany(this.companyId)?.code;
    var response = await this.situWarpApiClient.getIfcSettings(request);

    this.builderIfcFurnishingSettings = new Map<string, IfcToJsonFurnishingSettings>(response.settings!.furnishingSettings!.map(x => [x.furnishingName!, x]));
  }

  filterThemeLineItems() {
    var filterTerm = this.themeLineItemFilterTerm.toLowerCase();
    this.filteredGroupedLineItems = [];

    this.groupedLineItems.forEach(x => {

      var filterMatchesTag = [...x.tags]
        .some(y => y?.toLowerCase().includes(filterTerm));
      if (filterMatchesTag) {
        this.filteredGroupedLineItems.push(x);
        return;
      }

      var filterMatchesScope = x.scopes
        .some(y => y?.getName().toLowerCase().includes(filterTerm));
      if (filterMatchesScope) {
        this.filteredGroupedLineItems.push(x);
        return;
      }

      var filterMatchesLineItemType = this.getLineItemTypeName(x.lineItemTypeId)?.toLowerCase().includes(filterTerm);
      if (filterMatchesLineItemType) {
        this.filteredGroupedLineItems.push(x);
        return;
      }

      var filterMatchesProduct = this.getProductIds(x)
        .map(y => this.getProductById(y))
        .some(y => y?.name?.toLowerCase().includes(filterTerm) || this.getProductType(y?.productTypeId)?.name?.toLowerCase().includes(filterTerm));
        if (filterMatchesProduct) {
          this.filteredGroupedLineItems.push(x);
          return;
        }

    });
  }

  hasSelectedLineItemGroups(): boolean {
    return this.groupedLineItems.some(x => x.isSelected);
  }

  hasSelectedAllLineItemGroups(): boolean {
    return this.groupedLineItems.every(x => x.isSelected);
  }

  deleteSelectedLineItemGroups() {
    var selectedLineItemCount = this.groupedLineItems.filter(x => x.isSelected).length;

    const options: ConfirmDialogData = {
      title: `Are you sure you want to delete ${selectedLineItemCount} Line Item ${Utils.pluralize("Group", selectedLineItemCount)}?`,
      message: `The Line ${Utils.pluralize("Item", selectedLineItemCount)} will be deleted immediately.`,
      cancelText: 'Cancel',
      confirmText: 'Delete',
      actionIcon: 'priority_high',
      actionColour: 'warn'
    };
    this.dialogService.open(options);
    this._subscriptions.push(this.dialogService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.groupedLineItems = this.groupedLineItems.filter(x => !x.isSelected);

        this.filterThemeLineItems();
                
        this.debounceSaveChanges();

        this.showMessage(`Deleted ${selectedLineItemCount} Theme Line ${Utils.pluralize("Item", selectedLineItemCount)}.`);
      }
    }));
  }

  selectAllLineItemGroups() {
    this.groupedLineItems.forEach(x => {
      x.isSelected = true;
    });
  }

  deselectAllLineItemGroups() {
    this.groupedLineItems.forEach(x => {
      x.isSelected = false;
    });
  }

  async copyLineItemGroups() {
    var selectedLineItems: ThemeLineItem[] = [];

    this.groupedLineItems.forEach(x => {
      if (x.isSelected) {
        selectedLineItems.push(...x.lineItems);
      }
    });

    try {
      var content = JSON.stringify(selectedLineItems);
      var blob = new Blob([content], { type: this.clipboardLineItemMimeType });
      var data = [new ClipboardItem({ [this.clipboardLineItemMimeType]: Promise.resolve(blob) })];

      await navigator.clipboard.write(data);

      this.showMessage(`Copied ${selectedLineItems.length} Theme Line ${Utils.pluralize("Item", selectedLineItems.length)}.`);

      this.groupedLineItems.forEach(x => {
        x.isSelected = false;
      });
    } catch (err) {
      console.error('Failed to write to clipboard: ', err);
    }
  }

  async pasteLineItemGroups() {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const clipboardItem of clipboardItems) {
        if (clipboardItem.types.includes(this.clipboardLineItemMimeType)) {
          const blob = await clipboardItem.getType(this.clipboardLineItemMimeType);
          const text = await blob.text();
          
          if (text) {
            var lineItems: ThemeLineItem[] = [];
      
            var itemBuffer: any[] = JSON.parse(text);
            itemBuffer.forEach(x => {
              var lineItem = new ThemeLineItem(x);
              lineItem.id = Guid.create().toString();
              lineItems.push(lineItem);
            });
      
            this.groupLineItems(lineItems, true);

            this.getMissingProducts();

            this.filterThemeLineItems();
            
            this.debounceSaveChanges();

            this.showMessage(`Pasted ${lineItems.length} Theme Line ${Utils.pluralize("Item", lineItems.length)}.`);
            return;
          }
        }
      }
      console.log('No matching MIME type found.');
    } catch (err) {
      console.error('Failed to read from clipboard: ', err);
    }
  }

  getMissingProducts () {
    var productsToGet = new Set<string>();
    
    this.groupedLineItems
      .forEach(x => {
        var productIds = this.getProductIds(x);
        productIds.forEach(productId => {
          if (!this.productsMap?.has(productId)) {
            productsToGet.add(productId);
          }
        });
      });

      if (productsToGet.size === 0) {
        return;
      }

      this.productService.LoadProducts(this.theme?.companyId!, [...productsToGet])
      .then((products: Product2[]) => {
        products.forEach(x => this.productsMap?.set(x.id!, x));
      });
  }

  filterProductTypes(value: string): ProductType[] {
    var productTypes = Array.from(this.productTypesMap!.values());
    if (!value) {
      return productTypes;
    }

    var searchTerm = value.toLocaleLowerCase();
    return productTypes.filter(x => x.name!.toLowerCase().includes(searchTerm!));
  }

  onAutoFillTagProductTypeSelected(productTypeId: string): void {
    var selectedProductTypes = new Set<string>(this.autoFillTagSearchForm.value.selectedProductTypes);

    selectedProductTypes.add(productTypeId);

    this.productTypeInput!.nativeElement.value = '';
    this.autoFillTagProductTypeControl.setValue(null);

    this.autoFillTagSearchForm.patchValue({
      selectedProductTypes: Array.from(selectedProductTypes)
    });
  }
  
  onRemoveAutoFillTagProductType(productTypeId: string): void {
    var selectedProductTypes = new Set<string>(this.autoFillTagSearchForm.value.selectedProductTypes);

    selectedProductTypes.delete(productTypeId);

    this.autoFillTagSearchForm.patchValue({
      selectedProductTypes: Array.from(selectedProductTypes)
    });
  }

  toggleArea(productTypesInArea: ProductType[]) {
    var selectedProductTypes = new Set<string>(this.autoFillTagSearchForm.value.selectedProductTypes);

    var removeAll = productTypesInArea.every(x => selectedProductTypes.has(x.id!));
    
    for (const productType of productTypesInArea) {
      if (removeAll) {
        selectedProductTypes.delete(productType.id!);
      } else {
        selectedProductTypes.add(productType.id!);
      }
    }

    this.autoFillTagSearchForm.patchValue({
      selectedProductTypes: Array.from(selectedProductTypes)
    });
  }

  async autoFillTags() {
    this.showAutoFillTagOptions = false;
    const formValue = this.autoFillTagSearchForm.value;

    var options = new SearchThemeReplacementTagsOptions();
    options.filterByThemeId = formValue.filterByHomesInRelease ? this.themeId : undefined;
    options.filterActiveReleases = formValue.filterActiveReleases;
    options.filterByLatestIfcFiles = formValue.filterByLatestIfcFiles;
    options.useIfcTags = formValue.useIfcTags;
    options.ifcTagsOnly = formValue.ifcTagsOnly;
    options.standardTagsOnly = formValue.standardTagsOnly;
    options.tagGroupFilter = formValue.includeTagGroups;
    options.productTypeFilter = formValue.selectedProductTypes;

    var searchResult = await this.themeService.SearchThemeReplacementTags(this.companyId!, options);

    this.addTags(searchResult!);
  }

  async highlightObsoleteTags(){
    this.showAutoFillTagOptions = false;
    const formValue = this.autoFillTagSearchForm.value;

    var options = new SearchThemeReplacementTagsOptions();
    options.filterByThemeId = formValue.filterByHomesInRelease ? this.themeId : undefined;
    options.filterActiveReleases = formValue.filterActiveReleases;
    options.filterByLatestIfcFiles = formValue.filterByLatestIfcFiles;
    options.useIfcTags = true;
    options.ifcTagsOnly = formValue.ifcTagsOnly;
    options.standardTagsOnly = formValue.standardTagsOnly;
    options.tagGroupFilter = formValue.includeTagGroups;
    options.productTypeFilter = formValue.selectedProductTypes;

    var searchResult = await this.themeService.SearchThemeReplacementTags(this.companyId!, options);

    var currentTags = new Set<string>(searchResult.map(x => x.tag!));

    for (const tagDetails of searchResult) {
      var builderTag = this.getBuilderIfcSettingTag(tagDetails.tag);
      if (builderTag) {
        currentTags.add(builderTag);
      }
    }
    
    for (const groupedLineItem of this.groupedLineItems) {
      groupedLineItem.tagsToHighlight.clear();
      for (const tag of groupedLineItem.tags) {
        if (!tag || currentTags.has(tag)){
          continue;
        }

        groupedLineItem.isExpanded = true;
        groupedLineItem.tagsToHighlight.add(tag);
      }
    }
  }

  buildAreaGroups(productTypes: ProductType[]) {

    var kitchenPattern = /kitchen|sink|cooktop|oven|microwave|dishwasher|range hood|fridge|cabinet$|bench|tap/i;
    var kitchenAreaGroup = productTypes.filter((pt) => kitchenPattern.test(pt.name!));
    this.areaGroups['Kitchen'] = kitchenAreaGroup;

    var bedroomPattern = /bed|dresser/i;
    var bedroomAreaGroup = productTypes.filter((pt) => bedroomPattern.test(pt.name!));
    this.areaGroups['Bedrooms'] = bedroomAreaGroup;

    var bathroomsPattern = /bath|shower|basin|floor waste|mirror|tap|towel holder|soap holder/i;
    var bathroomsAreaGroup = productTypes.filter((pt) => bathroomsPattern.test(pt.name!));
    this.areaGroups['Bathrooms'] = bathroomsAreaGroup;

    var wcPattern = /toilet/i;
    var wcAreaGroup = productTypes.filter((pt) => wcPattern.test(pt.name!));
    this.areaGroups['WC'] = wcAreaGroup;

    var powderPattern = /basin|tap/i;
    var powderAreaGroup = productTypes.filter((pt) => powderPattern.test(pt.name!));
    this.areaGroups['Powder Room'] = powderAreaGroup;

    var livingAreasPattern = /couch|tv|coffee table|rug|plant|cabinet%|bookcase|table side|table console/i;
    var livingAreasGroup = productTypes.filter((pt) => livingAreasPattern.test(pt.name!));
    this.areaGroups['Living Areas'] = livingAreasGroup;

    var diningPattern = /dining/i;
    var diningAreaGroup = productTypes.filter((pt) => diningPattern.test(pt.name!));
    this.areaGroups['Dining Room'] = diningAreaGroup;

    var outdoorPattern = /outdoor|render|brick|cladding/i;
    var outdoorAreaGroup = productTypes.filter((pt) => outdoorPattern.test(pt.name!));
    this.areaGroups['Outdoors'] = outdoorAreaGroup;

    var laundryPattern = /washing machine|dryer|laundry|tap/i;
    var laundryAreaGroup = productTypes.filter((pt) => laundryPattern.test(pt.name!));
    this.areaGroups['Laundry'] = laundryAreaGroup;
  }

  addTags(scoredTags: ScoredThemeReplacementTag[]) {
    var existingTags = new Set<string>();
    this.groupedLineItems.forEach(x => {
      x.tags.forEach(tag => {
        if (tag !== undefined) {
          existingTags.add(tag);
        }
      });
    });

    var existingTagCount = 0;
    var addedTagCount = 0;

    for (const scoredTag of scoredTags) {
      if (!scoredTag) {
        continue;
      }

      // Don't add the tag if it is already used
      if (existingTags.has(scoredTag.tag!)) {
        existingTagCount++;
        continue;
      }

      addedTagCount++;

      var newItem = new ThemeLineItem();
      newItem.properties = [];
      newItem.tag = scoredTag.tag!;

      var lineItemGroup = new LineItemGroup(newItem, scoredTag.tagGroup!);

      this.groupedLineItems.unshift(lineItemGroup); // Add the new group to the start of the list of groups
      this.filteredGroupedLineItems.unshift(lineItemGroup);
    }
    
    this.showMessage(`Added ${addedTagCount} ${Utils.pluralize("tag", addedTagCount)}. ${existingTagCount} already existed.`);
  }

  private showMessage(message: string) {
    this.snackBar.open(message, 'Dismiss', {
      duration: 3000
    });
  }

  timeAgoString(date?: Date): string {
    return Utils.timeAgoString(date);
  }
}

export class LineItemGroup {
  key: string = Guid.create().toString();
  lineItems: ThemeLineItem[] = [];

  // These properties are to be shared by all line items it this group
  // This way a properties need to only be change once for all to get the update
  lineItemProperties: { [key: string]: any; };

  lineItemTypeId: string;
  tagGroup?: string;

  tags = new Set<string | undefined>();
  scopes: Scope[] = [];

  isExpanded: boolean = false;
  isSelected: boolean = false;

  allValidTags?: ScoredThemeReplacementTag[];
  filteredTags: ScoredThemeReplacementTag[] = [];
  remainingFilteredTags: ScoredThemeReplacementTag[] = [];
  hasRequestedTagOptions = false;
  showTagSelection = false;
  showRemainingTags = false;
  autoShowRemainingTags = false;
  tagSearchTerm: string | undefined;
  tagToEdit: string | undefined;
  tagToEditOriginal: string | undefined;
  tagsToHighlight = new Set<string>();

  filteredScopes: RoomType[] = [];
  showScopeSelection = false;
  scopeToEdit: Scope | undefined;
  scopeSearchTerm: string | undefined;

  showLineItemTypeSwap = false;

  forceGroupSplit = false;

  constructor(lineItem: ThemeLineItem, tagGroup?: string) {
    this.lineItemTypeId = lineItem.lineItemTypeId!;
    this.lineItemProperties = lineItem.properties!;
    this.tagGroup = tagGroup

    this.addToGroup(lineItem);
  }

  fitsGroup(testLineItem: ThemeLineItem) : boolean {
    if (this.lineItemTypeId !== testLineItem.lineItemTypeId) {
      return false; // Line Item type must be the same
    }

    if (this.forceGroupSplit) {
      return false; // This line items has been forced to be split
    }

    var propertiesAreTheSame = this.propertiesAreTheSame(testLineItem.properties!);
    if (!propertiesAreTheSame) {
      return false; // all properties must be the identical
    }

    var hasSameTag = this.tags.has(testLineItem.tag);
    if (!hasSameTag) {
      return false; // all Line Items must have the same tag
    }

    return true;
  }

  propertiesAreTheSame(properties: { [key: string]: any; }) : boolean {
    const groupPropertyKeys = Object.keys(this.lineItemProperties);
    const testPropertyKeys = Object.keys(properties);

    if (groupPropertyKeys.length !== testPropertyKeys.length) {
      return false; // Line Item properties must have the same count
    }

    var allPropertyValuesMatch = groupPropertyKeys.every(key => this.lineItemProperties[key] === properties[key]);
    if (!allPropertyValuesMatch) {
      return false; // all Line Item properties must have the same value
    }

    return true;
  }

  addToGroup(lineItem: ThemeLineItem) {
    var hasSameScope = this.scopes.find(x => x.isEqual(lineItem.scope, lineItem.scopeVariant));
    if (hasSameScope) {
      // Don't add a line item with the same properties, scope and scope variant a second time.
      return;
    }

    this.lineItems.push(lineItem);

    this.tags.add(lineItem.tag);
    this.scopes.push(new Scope(lineItem.scope, lineItem.scopeVariant));

    if (this.scopes.length > 1 && this.scopes.some(x => x.isEqual(RoomType.NotSet, ScopeVariant.Default))) {
      // Remove the `RoomType.NotSet` if there is more than one scope in the group
      // `RoomType.NotSet` can't be used when other scopes are set
      this.scopes = this.scopes.filter(x => !x.isEqual(RoomType.NotSet, ScopeVariant.Default));
      this.lineItems = this.lineItems.filter(x => x.scope !== RoomType.NotSet);
    }

    this.sortScopes();

    lineItem.properties = this.lineItemProperties;
  }

  canMergeGroup(group: LineItemGroup) {
    if (!this.lineItemTypeId || !group.lineItemTypeId) {
      return false; // Line Item type must be set
    }

    if (this.lineItemTypeId !== group.lineItemTypeId) {
      return false; // Line Item type must be the same
    }

    if (this.forceGroupSplit) {
      return false; // This line items has been forced to be split
    }

    var propertiesAreTheSame = this.propertiesAreTheSame(group.lineItemProperties);
    if (!propertiesAreTheSame) {
      return false; // all properties must be the identical
    }

    if (this.scopes.length !== group.scopes.length) {
      return false; // scopes must have the same count
    }

    var allScopesMatch = this.scopes.every(x => group.scopes.some(y => y.isEqual(x.scope, x.scopeVariant)));

    if (!allScopesMatch) {
      return false; // scopes must be the same
    }

    return true;
  }

  mergeGroup(group: LineItemGroup) {
    group.lineItems.forEach(x => {
      x.properties = this.lineItemProperties;
      this.lineItems.push(x);
    });

    group.tags.forEach(x => {
      this.tags.add(x);
    });
  }

  addTagToGroup(scoredTag?: string | ScoredThemeReplacementTag) {
    var tag: string | undefined;
    if (scoredTag instanceof ScoredThemeReplacementTag) {
      tag = scoredTag?.tag;
    } else {
      tag = scoredTag;
    }

    var hasSameTag = this.tags.has(tag);
    if (hasSameTag) {
      // Don't add the same tag a second time.
      return;
    }

    if (!this.tagGroup && scoredTag instanceof ScoredThemeReplacementTag) {
      this.tagGroup = scoredTag.tagGroup;
      this.filterTags();
    }

    this.tags.add(tag)

    this.scopes.forEach(scope => {
      let newItem = new ThemeLineItem();
      newItem.id = Guid.create().toString();
      newItem.lineItemTypeId = this.lineItemTypeId;
      newItem.tag = tag;
      newItem.scope = scope.scope;
      newItem.scopeVariant = scope.scopeVariant;
      newItem.properties = this.lineItemProperties;
      this.lineItems.push(newItem);
    });

    if (scoredTag) {
      // Remove the empty tag if there is more than one tag in the group
      // A empty tag can't be used when other tags are set
      this.tags.delete(undefined);
      this.lineItems = this.lineItems.filter(x => x.tag);
    }

    this.filterTags();
  }

  removeTagFromGroup(tag: string | undefined) {
    this.tags.delete(tag);
    this.lineItems = this.lineItems.filter(x => x.tag !== tag);

    if (this.lineItems.length === 0) {
      // Add empty tag if there are no tags in the group
      this.addTagToGroup(undefined);
    }

    this.filterTags();
  }

  addScopeToGroup(scope: RoomType = RoomType.NotSet, scopeVariant: ScopeVariant = ScopeVariant.Default) : Scope {
    var hasSameScope = this.scopes.find(x => x.isEqual(scope, scopeVariant));
    if (hasSameScope) {
      // Don't add the same scope and scope variant a second time.
      return hasSameScope;
    }

    var newScope = new Scope(scope, scopeVariant);
    this.scopes.push(newScope)

    this.tags.forEach(tag => {
      let newItem = new ThemeLineItem(this.lineItems[0]);
      newItem.id = Guid.create().toString();
      newItem.lineItemTypeId = this.lineItemTypeId;
      newItem.tag = tag;
      newItem.scope = newScope.scope;
      newItem.scopeVariant = newScope.scopeVariant;
      newItem.properties = this.lineItemProperties;
      this.lineItems.push(newItem);
    });

    if (scope !== RoomType.NotSet) {
      // Remove the `RoomType.NotSet` if there is more than one scope in the group
      // `RoomType.NotSet` can't be used when other scopes are set
      this.scopes = this.scopes.filter(x => !x.isEqual(RoomType.NotSet, ScopeVariant.Default));
      this.lineItems = this.lineItems.filter(x => x.scope !== RoomType.NotSet);
    }

    this.sortScopes();

    this.filterScopes();

    return newScope;
  }


  removeScopeFromGroup(scope: RoomType = RoomType.NotSet, scopeVariant: ScopeVariant = ScopeVariant.Default) {
    this.scopes = this.scopes.filter(x => !x.isEqual(scope, scopeVariant));
    this.lineItems = this.lineItems.filter(x => !(x.scope === scope && x.scopeVariant === scopeVariant));

    if (this.scopes.length === 0) {
      // Add the `RoomType.NotSet` if there are no scopes in the group
      this.addScopeToGroup(RoomType.NotSet, ScopeVariant.Default);
    }
    else {
      this.sortScopes();

      this.filterScopes();
    }
  }

  sortScopes() {
    Utils.dynamicSort(this.scopes, x => x.getName());
  }

  changeProperty(key: string, value: any) {
    this.lineItemProperties[key] = value;
  }

  getDisplayTags() : string[] {
    return [...this.tags].filter(x => x != undefined) as string[];
  }

  toggleRemainingTags() {
    this.showRemainingTags = !this.showRemainingTags;
    this.autoShowRemainingTags = false;
  }

  filterTags() {
    if (!this.allValidTags) {
      return;
    }

    var hasHighScore = this.allValidTags.some(x => x.score! > 1);
    var filtered = this.allValidTags
      .filter(x => !this.tags.has(x.tag));

    if (this.tagGroup) {
      var filtered = filtered
        .filter(x => x.tagGroup === this.tagGroup);
    }

    if (this.tagSearchTerm) {
      var filterValue = this.tagSearchTerm.toLowerCase();
      filtered = filtered
        .filter(x => x.tag!.toLowerCase().includes(filterValue));
    }

    var highScore: ScoredThemeReplacementTag[] = [];
    var lowScore: ScoredThemeReplacementTag[] = [];

    filtered.forEach(x => {
      if (x.score! > 1) {
        highScore.push(x);
      } else {
        lowScore.push(x);
      }
    });

    if (hasHighScore) {
      this.filteredTags = highScore;
      this.remainingFilteredTags = lowScore;
    } else {
      this.filteredTags = lowScore;
      this.remainingFilteredTags = [];
    }

    if (this.filteredTags.length <= 0 && this.remainingFilteredTags.length > 0) {
      if (!this.showRemainingTags) {
        this.showRemainingTags = true;
        this.autoShowRemainingTags = true;
      }
    }
    else if (this.autoShowRemainingTags) {
      this.showRemainingTags = false;
      this.autoShowRemainingTags = false;
    }
  }
  
  getDisplayScopes(showNotSet: boolean = false) : Scope[] {
    if (showNotSet) {
      return this.scopes;
    } else {
      return this.scopes.filter(x => x.scope !== RoomType.NotSet || x.scopeVariant !== ScopeVariant.Default);
    }
  }

  filterScopes() {
    var allScopes = Object.entries(RoomType)
      .filter(([key, value]) => typeof value === 'number')
      .map(([key, value]) => value as number);

    // Filter out existing scopes
    var filtered = allScopes
      .filter(x => x !== 0 && !this.scopes.some(e => e.scope === x && e.scopeVariant === ScopeVariant.Default));

    if (this.scopeSearchTerm) {
      var filterValue = this.scopeSearchTerm.toLowerCase();
      filtered = filtered
        .filter(x => Scope.buildName(x).toLowerCase().includes(filterValue));
    }

    Utils.dynamicSort(filtered, x =>  Scope.buildName(x));
  
    this.filteredScopes = filtered;
  }

  showTagEdit(tagToEdit: string) {
    this.showTagSelection = false;
    this.tagToEdit = tagToEdit;
    this.tagToEditOriginal = tagToEdit;
  }

  cancelTagEdit() {
    this.showTagSelection = false;
    this.removeTagFromGroup(this.tagToEdit);
    this.addTagToGroup(this.tagToEditOriginal);
    this.tagToEdit = undefined;
    this.tagToEditOriginal = undefined;
  }

  editTag(tagToEdit: string, ifcTag: string) {
    this.tagToEdit = ifcTag;
    this.removeTagFromGroup(tagToEdit);
    this.addTagToGroup(ifcTag);
  }

  showScopeEdit(scopeToEdit: Scope) {
    this.showScopeSelection = false;
    this.scopeToEdit = scopeToEdit;
  }

  editScopeVariant(scopeVariant: ScopeVariant) {
    var originalScope = this.scopeToEdit!;
    this.scopeToEdit = this.addScopeToGroup(originalScope.scope, scopeVariant);

    this.removeScopeFromGroup(originalScope.scope, originalScope.scopeVariant);
  }
}

class Scope {
  scope: RoomType;
  scopeVariant: ScopeVariant;

  constructor(scope: RoomType = RoomType.NotSet, scopeVariant: ScopeVariant = ScopeVariant.Default) {
    this.scope = scope;
    this.scopeVariant = scopeVariant;
  }

  isEqual(scope: RoomType = RoomType.NotSet, scopeVariant: ScopeVariant = ScopeVariant.Default) : boolean {
    return this.scope === scope && this.scopeVariant === scopeVariant;
  }

  getName() : string {
    return Scope.buildName(this.scope, this.scopeVariant);
  }

  static buildName(scope: RoomType, scopeVariant: ScopeVariant = ScopeVariant.Default) : string {
    var s = scope === RoomType.NotSet ? "All Rooms" : RoomType[scope];

    if (scopeVariant !== ScopeVariant.Default) {
      s = `${s} ${ScopeVariant[scopeVariant]}`;
    }

    return s;
  }

  getScopeVariants() : ScopeVariant[] {
    const allScopeVariants = Object.entries(ScopeVariant)
      .filter(([key, value]) => typeof value === 'number')
      .map(([key, value]) => value as number);
      
      return allScopeVariants;
  }
}
